import React from 'react';
import { SectionFieldCollection, SectionFieldDTO } from '../magic-report.type';
import { MagicReportSection } from './MagicReportSection';
import { PROGRESS_DESCRIPTION_SECTION_CONFIG } from './sections.config';
import { CommentsSection } from './CommentsSection';
import { getCommentsField } from './section-utils';
import { DeclarativeLayoutRenderer } from './DeclarativeLayoutRenderer';

interface ProgressDescriptionSectionProps {
  magicReportId: string;
}

/**
 * Progress Description Section Content
 * Now using declarative config-driven rendering!
 */
const ProgressDescriptionSectionContent: React.FC<{
  data: SectionFieldCollection;
  handleFieldChange: (fieldName: string, updates: Partial<SectionFieldDTO>) => void;
  getField: (fieldName: string) => SectionFieldDTO | undefined;
  onBook?: () => number;
}> = ({ data, handleFieldChange, getField, onBook }) => {
  const commentsField = getCommentsField(data, PROGRESS_DESCRIPTION_SECTION_CONFIG.config);

  if (!PROGRESS_DESCRIPTION_SECTION_CONFIG.layout) {
    return <div>Layout configuration missing</div>;
  }

  // Handle field deletion (clear content)
  const handleDeleteField = (fieldName: string) => {
    handleFieldChange(fieldName, { value: '', addToActionItems: false });
  };

  return (
    <>
      {/* Config-driven layout rendering - just pass the config! */}
      <DeclarativeLayoutRenderer
        layout={PROGRESS_DESCRIPTION_SECTION_CONFIG.layout}
        getField={getField}
        handleFieldChange={handleFieldChange}
        onDeleteField={handleDeleteField}
        onBook={onBook}
      />

      {/* Comments Section */}
      {commentsField && (
        <CommentsSection
          commentsField={commentsField}
          onChange={updated => handleFieldChange('Comments', updated)}
          title='Comments'
          onBook={onBook}
        />
      )}
    </>
  );
};

/**
 * Progress Description Section
 * Now fully config-driven! Layout is defined in sections.config.ts
 */
export const ProgressDescriptionSection: React.FC<ProgressDescriptionSectionProps> = ({ magicReportId }) => {
  return (
    <MagicReportSection
      magicReportId={magicReportId}
      sectionConfig={PROGRESS_DESCRIPTION_SECTION_CONFIG}
      customization={{
        renderContent: (data, handleFieldChange, getField, onBook) => (
          <ProgressDescriptionSectionContent data={data} handleFieldChange={handleFieldChange} getField={getField} onBook={onBook} />
        ),
      }}
    />
  );
};
